<template>
  <v-card width="428" :loading="isLoading">
    <v-card-title class="primary--text pa-4">
      Notification profiles
    </v-card-title>
    <v-divider/>
    <v-list class="pa-0 pb-4">
      <v-list-item
        dense
        :key="item.key"
        v-for="item in availableNotificationProfiles"
      >
        <v-checkbox
          v-model="notificationProfiles"
          :hide-details="true"
          :value="item.key"
          :label="item.title"
          color="primary"
          :ripple="false"
        ></v-checkbox>
      </v-list-item>
    </v-list>

    <v-divider />
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn text @click="close">Cancel</v-btn>
      <v-btn
        data-test="addProfile"
        text
        color="primary"
        @click="saveHandler"
        :loading="loading"
        >Save
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import {
  computed,
  defineComponent,
  onMounted,
  ref
} from '@vue/composition-api';
import { useMutation, useQuery, useResult } from '@vue/apollo-composable';
import schemasGql from '@/api/schemas/list.graphql';
import gql from '@/api/objects/item.graphql';
import authSchemas from '@/api/auth/schemas.graphql';
import useAuth from "@/api/auth";

export default defineComponent({
  name: 'ChangeNotificationProfiles',
  props: {
    profileId: {
      type: String,
      required: true
    }
  },
  setup(props, { root, emit }) {
    const notificationProfiles = ref([]);
    const { user } = new useAuth();
    const { mutate: linkObjToObj } = useMutation(gql.connectObjectsToObject);
    const { mutate: createObject } = useMutation(
      gql.createObjectWithProperties
    );
    const { mutate: deleteMany } = useMutation(gql.removeMany);

    const { result, loading, onResult } = useQuery(
      authSchemas.loadProfile,
      {
        id: props.profileId
      },
      {
        fetchPolicy: 'no-cache',
        nextFetchPolicy: 'no-cache'
      }
    );

    onResult(({ data }) => {
      data.object.objectsToObjectsByObject1Id.forEach(item => {
        notificationProfiles.value.push(item.object2.deliveryChannel);
      });
    });

    const { result: notificationProfile } = useQuery(
      schemasGql.loadPureWithProperties,
      {
        filter: {
          mTags: {
            contains: ['application', 'notification profile']
          }
        }
      },
      {
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only'
      }
    );

    const currentProfiles = computed(() =>
      result.value.object.objectsToObjectsByObject1Id.map(
        item => item.object2.deliveryChannel
      )
    );

    const schemaTags = computed(() => result.value.object.schemaTags);

    const currentProfilesWithIds = computed(() =>
      result.value.object.objectsToObjectsByObject1Id.map(item => item)
    );

    const schemaNotificationProfile = computed(
      () => notificationProfile.value?.schemata?.[0]
    );

    const availableNotificationProfiles = computed(
      () =>
        notificationProfile.value?.schemata?.[0]?.schemaProperties?.find(
          item => item.key === 'settingsDeliveryChannel'
        )?.valueSet?.list || []
    );

    const schema = ref(null);
    const close = () => {
      emit('close');
    };

    const saveHandler = () => {
      let addObjects = notificationProfiles.value.filter(
        i => !currentProfiles.value.includes(i)
      );

      let deleteObjects = currentProfiles.value.filter(
        i => !notificationProfiles.value.includes(i)
      );

      let deleteIds = [];

      currentProfilesWithIds.value.forEach(item => {
        if (deleteObjects.includes(item.object2.deliveryChannel)) {
          deleteIds.push(item.object2.id);
        }
      });

      const createPromises = [];

      addObjects.forEach(item => {
        const name = `${schemaTags.value[1]
          .charAt(0)
          .toUpperCase()}${schemaTags.value[1].slice(1)}`;
        createPromises.push(
          createObject({
            input: {
              enabled: true,
              schemaId: schemaNotificationProfile.value.id,
              name: `${name} Notification Profile (${item})`,
              editorGroup: user.value.defaultEditorgroup,
              keyedProperties: [
                {
                  propertyKey: 'settingsDeliveryChannel',
                  value: item
                }
              ]
            }
          })
        );
      });

      Promise.all(createPromises)
        .then(res => {
          const linkPromises = [];

          res.forEach(item => {
            linkPromises.push(
              linkObjToObj({
                input: {
                  objectsToObject: {
                    object1Id: props.profileId,
                    object2Id: item.data.createObjectWithProperties.uuid
                  }
                }
              })
            );
          });

          return Promise.all(linkPromises);
        })
        .then(() => {
          if (deleteIds.length > 0) {
            const input = {
              mnPatch: deleteIds.map(item => ({ id: item }))
            };

            return deleteMany({
              input
            });
          } else {
            return Promise.resolve();
          }
        })
        .then(() => {
          close();
        });
    };

    const handleClose = data => {
      const indexDeleting = notificationProfiles.value.findIndex(
        item => item.key === data.key
      );
      notificationProfiles.value.splice(indexDeleting, 1);
    };

    return {
      handleClose,
      saveHandler,
      close,
      schema,
      loading: false,
      schemaNotificationProfile,
      availableNotificationProfiles,
      notificationProfiles,
      currentProfiles,
      isLoading: loading
    };
  }
});
</script>
